import { Link } from "react-router-dom";
import {
  HeartHandshake, Clock, BookOpen, PhoneCall, Eye, Volume2, Users, CheckCircle2, Phone,
} from "lucide-react";
import { CONTACT } from "../data";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { CTABand } from "../components/CTABand";

const PROMISES = [
  { icon: Clock, title: "We take our time", text: "No rushing, no watching the clock. We go at a pace that feels comfortable for you." },
  { icon: BookOpen, title: "Simple written notes", text: "We leave you clear, large‑print step‑by‑step notes you can refer back to anytime." },
  { icon: PhoneCall, title: "Call us anytime", text: "Have a question after we leave? Just pick up the phone. We're always glad to help." },
  { icon: HeartHandshake, title: "Kind & respectful", text: "There are no silly questions. We treat every visit with warmth and respect." },
];

const HELP_WITH = [
  { icon: Eye, title: "Making screens easier to read", text: "Bigger text, higher contrast, and brighter displays." },
  { icon: Volume2, title: "Turning up the volume", text: "Louder rings, clearer calls, and captions where helpful." },
  { icon: Users, title: "Video calling family", text: "See your children and grandchildren with a single tap." },
];

export default function SeniorHelp() {
  return (
    <div data-testid="senior-help-page">
      <PageHero
        eyebrow="Especially for Seniors"
        title="Patient, respectful tech help — at your pace"
        subtitle="Technology should make life easier, not harder. We specialize in helping seniors feel calm, capable, and confident with their devices."
      >
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <a
            href={CONTACT.phoneHref}
            data-testid="senior-call-now"
            className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-accent text-white font-bold text-lg hover:bg-[hsl(196,82%,27%)] transition-colors"
          >
            <Phone className="h-5 w-5" /> Call {CONTACT.phone}
          </a>
          <Link
            to="/contact"
            data-testid="senior-book"
            className="inline-flex items-center justify-center h-14 px-8 rounded-full bg-primary text-white font-bold text-lg hover:bg-slate-800 transition-colors"
          >
            Book a Friendly Visit
          </Link>
        </div>
      </PageHero>

      {/* Promise */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <Reveal>
          <h2 className="font-heading font-extrabold text-foreground text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-snug max-w-2xl">
            Our promise to you
          </h2>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 gap-6">
          {PROMISES.map((p, i) => (
            <Reveal key={p.title} delay={(i % 2) * 0.08}>
              <div className="h-full rounded-2xl border border-border bg-card p-8 flex gap-5 items-start hover:border-accent/40 transition-colors">
                <span className="grid place-items-center h-14 w-14 rounded-2xl bg-accent/10 text-accent shrink-0">
                  <p.icon className="h-7 w-7" />
                </span>
                <div>
                  <h3 className="font-heading font-bold text-xl text-foreground mb-2">{p.title}</h3>
                  <p className="text-muted-foreground text-lg leading-relaxed">{p.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Common help + image */}
      <section className="bg-secondary">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img
              src="https://images.unsplash.com/photo-1758691030826-86a149b6278b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Senior gentleman comfortably using his smartphone"
              className="rounded-[2rem] shadow-2xl w-full h-[400px] lg:h-[480px] object-cover"
            />
          </Reveal>
          <Reveal delay={0.12}>
            <h2 className="font-heading font-extrabold text-foreground text-3xl sm:text-4xl tracking-tight leading-snug">
              Little changes, big difference
            </h2>
            <p className="mt-5 text-muted-foreground text-lg leading-relaxed">
              A few thoughtful adjustments can make your phone or computer far
              easier and more enjoyable to use.
            </p>
            <div className="mt-8 space-y-5">
              {HELP_WITH.map((h) => (
                <div key={h.title} className="flex gap-4 items-start">
                  <span className="grid place-items-center h-12 w-12 rounded-xl bg-card border border-border text-accent shrink-0">
                    <h.icon className="h-6 w-6" />
                  </span>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-foreground">{h.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{h.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Reassurance band */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-20 lg:py-24 text-center">
        <Reveal>
          <HeartHandshake className="h-12 w-12 text-accent mx-auto mb-6" />
          <p className="font-heading font-bold text-2xl sm:text-3xl text-foreground leading-snug">
            “You're never too old to learn, and there's no such thing as a silly
            question. We'll be right beside you, every step of the way.”
          </p>
          <ul className="mt-10 grid sm:grid-cols-3 gap-4 text-left">
            {[
              "In‑home visits across Columbus",
              "Help over the phone or video",
              "Family can join in too",
            ].map((t) => (
              <li key={t} className="flex items-center gap-3 justify-center sm:justify-start text-slate-300 font-medium">
                <CheckCircle2 className="h-5 w-5 text-accent shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      <CTABand />
    </div>
  );
}
